import React, { useEffect, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import { useFormik } from 'formik';
import { Calendar as DatePicker } from 'react-date-range';
import USERS, { getUserDataWithUsername } from '../../../common/data/userDummyData';
import eventList from '../../../common/data/events';
import {
	CalendarTodayButton,
	CalendarViewModeButtons,
	getLabel,
	getUnitType,
	getViews,
} from '../../extras/calendarHelper';
import { getServiceDataWithServiceName } from '../../../common/data/serviceDummyData';
import Icon from '../../icon/Icon';
import Avatar, { AvatarGroup } from '../../Avatar';
import Card, {
	CardActions,
	CardBody,
	CardHeader,
	CardLabel,
	CardSubTitle,
	CardTitle,
} from '../../bootstrap/Card';
import Tooltips from '../../bootstrap/Tooltips';
import useDarkMode from '../../../hooks/useDarkMode';

const localizer = momentLocalizer(moment);
const now = new Date();

const MyEvent = (data) => {
	const { darkModeStatus } = useDarkMode();

	const { event } = data;
	return (
		<div className='row g-2'>
			<div className='col text-truncate'>
				{event?.icon && <Icon icon={event?.icon} size='lg' className='me-2' />}
				{event?.name}
			</div>
			{event?.user?.src && (
				<div className='col-auto'>
					<div className='row g-1 align-items-baseline'>
						<div className='col-auto'>
							<Avatar src={event?.user?.src} srcSet={event?.user?.srcSet} size={18} />
						</div>
						<small
							className={classNames('col-auto text-truncate', {
								'text-dark': !darkModeStatus,
								'text-white': darkModeStatus,
							})}>
							{event?.user?.name}
						</small>
					</div>
				</div>
			)}
			{event?.users && (
				<div className='col-auto'>
					<AvatarGroup size={18}>
						{event.users.map((user) => (
							// eslint-disable-next-line react/jsx-props-no-spreading
							<Avatar key={user.src} {...user} />
						))}
					</AvatarGroup>
				</div>
			)}
		</div>
	);
};

const MyWeekEvent = (data) => {
	const { darkModeStatus } = useDarkMode();

	const { event } = data;
	return (
		<div className='row g-2'>
			<div className='col-12 text-truncate'>
				{event?.icon && <Icon icon={event?.icon} size='lg' className='me-2' />}
				{event?.name}
			</div>
			{event?.user && (
				<div className='col-12'>
					<div className='row g-1 align-items-baseline'>
						<div className='col-auto'>
							{/* eslint-disable-next-line react/jsx-props-no-spreading */}
							<Avatar {...event?.user} size={18} />
						</div>
						<small
							className={classNames('col-auto text-truncate', {
								'text-dark': !darkModeStatus,
								'text-white': darkModeStatus,
							})}>
							{event?.user?.name}
						</small>
					</div>
				</div>
			)}
			{event?.users && (
				<div className='col-12'>
					<AvatarGroup size={18}>
						{event.users.map((user) => (
							// eslint-disable-next-line react/jsx-props-no-spreading
							<Avatar key={user.src} {...user} />
						))}
					</AvatarGroup>
				</div>
			)}
		</div>
	);
};

const MyEventDay = (data) => {
	const { event } = data;
	return (
		<Tooltips
			title={`${event?.name} / ${moment(event.start).format('LT')} - ${moment(
				event.end,
			).format('LT')}`}>
			<div className='row g-2'>
				{event?.user?.src && (
					<div className='col-auto'>
						<Avatar src={event?.user?.src} srcSet={event?.user?.srcSet} size={16} />
					</div>
				)}
				{event?.users && (
					<div className='col'>
						<AvatarGroup size={16}>
							{event.users.map((user) => (
								// eslint-disable-next-line react/jsx-props-no-spreading
								<Avatar key={user.src} {...user} />
							))}
						</AvatarGroup>
					</div>
				)}
				<small className='col text-truncate'>
					{event?.icon && <Icon icon={event?.icon} size='lg' className='me-2' />}
					{event?.name}
				</small>
			</div>
		</Tooltips>
	);
};

const CalendarPage = () => {
	const { darkModeStatus, themeStatus } = useDarkMode();

	// BEGIN :: Calendar
	// Active employee
	const [employeeList, setEmployeeList] = useState({
		[USERS.JOHN.username]: true,
		[USERS.ELLA.username]: true,
		[USERS.RYAN.username]: true,
		[USERS.GRACE.username]: true,
	});
	// Events
	const [events, setEvents] = useState(eventList);

	// FOR DEV
	useEffect(() => {
		setEvents(eventList);
		return () => {};
	}, []);

	// Selected Event
	const [eventItem, setEventItem] = useState(null);
	// Calendar View Mode
	const [viewMode, setViewMode] = useState(Views.MONTH);
	// Calendar Date
	const [date, setDate] = useState(new Date());
	// Item edit panel status
	const [toggleInfoEventCanvas, setToggleInfoEventCanvas] = useState(false);
	const setInfoEvent = () => setToggleInfoEventCanvas(!toggleInfoEventCanvas);
	const [eventAdding, setEventAdding] = useState(false);

	// Calendar Unit Type
	const unitType = getUnitType(viewMode);
	// Calendar Date Label
	const calendarDateLabel = getLabel(date, viewMode);

	// Change view mode
	const handleViewMode = (e) => {
		setDate(moment(e)._d);
		setViewMode(Views.DAY);
	};

	// View modes; Month, Week, Work Week, Day and Agenda
	const views = getViews();

	// New Event
	const handleSelect = ({ start, end }) => {
		setEventAdding(true);
		setEventItem({ start, end });
	};

	useEffect(() => {
		if (eventAdding) {
			setInfoEvent();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventAdding]);

	// eslint-disable-next-line no-unused-vars
	const eventStyleGetter = (event, start, end, isSelected) => {
		const isActiveEvent = start <= now && end >= now;
		const isPastEvent = end < now;
		const color = isActiveEvent ? 'success' : event.color;

		return {
			className: classNames({
				[`bg-l${darkModeStatus ? 'o25' : '10'}-${color} text-${color}`]: color,
				'border border-success': isActiveEvent,
				'opacity-50': isPastEvent,
			}),
		};
	};

	const formik = useFormik({
		initialValues: {
			eventName: '',
			eventStart: '',
			eventEnd: '',
			eventEmployee: '',
		},
		onSubmit: (values) => {
			if (eventAdding) {
				setEvents((prevEvents) => [
					...prevEvents,
					{
						id: values.eventStart,
						...getServiceDataWithServiceName(values.eventName),
						end: values.eventEnd,
						start: values.eventStart,
						user: { ...getUserDataWithUsername(values.eventEmployee) },
					},
				]);
			}
			setToggleInfoEventCanvas(false);
			setEventAdding(false);
			setEventItem(null);
			formik.setValues({});
		},
	});

	useEffect(() => {
		if (eventItem)
			formik.setValues({
				...formik.values,
				eventId: eventItem.id || null,
				eventName: eventItem.name,
				eventStart: moment(eventItem.start).format(),
				eventEnd: moment(eventItem.end).format(),
				eventEmployee: eventItem?.user?.username,
			});
		return () => {};
		//	eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventItem]);
	// END:: Calendar
	return (
		<div>
			<div>
				<Card stretch style={{ minHeight: 500 }}>
					<CardHeader>
						<CardActions>
							<CalendarTodayButton
								unitType={unitType}
								date={date}
								setDate={setDate}
								viewMode={viewMode}
							/>
						</CardActions>
						<CardActions>
							<CalendarViewModeButtons
								setViewMode={setViewMode}
								viewMode={viewMode}
							/>
						</CardActions>
					</CardHeader>
					<CardBody isScrollable>
						<Calendar
							selectable
							toolbar={false}
							localizer={localizer}
							events={events.filter((i) => employeeList[i.user?.username])}
							defaultView={Views.WEEK}
							views={views}
							view={viewMode}
							date={date}
							onNavigate={(_date) => setDate(_date)}
							scrollToTime={new Date(1970, 1, 1, 6)}
							defaultDate={new Date()}
							onSelectEvent={(event) => {
								setInfoEvent();
								setEventItem(event);
							}}
							onSelectSlot={handleSelect}
							onView={handleViewMode}
							onDrillDown={handleViewMode}
							components={{
								event: MyEvent, // used by each view (Month, Day, Week)
								week: {
									event: MyWeekEvent,
								},
								work_week: {
									event: MyWeekEvent,
								},
							}}
							eventPropGetter={eventStyleGetter}
						/>
					</CardBody>
				</Card>
			</div>
		</div>
	);
};

export default CalendarPage;

export const NotesPage = () => {
    const { darkModeStatus, themeStatus } = useDarkMode();

	// BEGIN :: Calendar
	// Active employee
	const [employeeList, setEmployeeList] = useState({
		[USERS.JOHN.username]: true,
		[USERS.ELLA.username]: true,
		[USERS.RYAN.username]: true,
		[USERS.GRACE.username]: true,
	});
	// Events
	const [events, setEvents] = useState(eventList);

	// FOR DEV
	useEffect(() => {
		setEvents(eventList);
		return () => {};
	}, []);

	// Selected Event
	const [eventItem, setEventItem] = useState(null);
	// Calendar View Mode
	const [viewMode, setViewMode] = useState(Views.MONTH);
	// Calendar Date
	const [date, setDate] = useState(new Date());
	// Item edit panel status
	const [toggleInfoEventCanvas, setToggleInfoEventCanvas] = useState(false);
	const setInfoEvent = () => setToggleInfoEventCanvas(!toggleInfoEventCanvas);
	const [eventAdding, setEventAdding] = useState(false);

	// Calendar Unit Type
	const unitType = getUnitType(viewMode);
	// Calendar Date Label
	const calendarDateLabel = getLabel(date, viewMode);

	// Change view mode
	const handleViewMode = (e) => {
		setDate(moment(e)._d);
		setViewMode(Views.DAY);
	};

	// View modes; Month, Week, Work Week, Day and Agenda
	const views = getViews();

	// New Event
	const handleSelect = ({ start, end }) => {
		setEventAdding(true);
		setEventItem({ start, end });
	};

	useEffect(() => {
		if (eventAdding) {
			setInfoEvent();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventAdding]);

	// eslint-disable-next-line no-unused-vars
	const eventStyleGetter = (event, start, end, isSelected) => {
		const isActiveEvent = start <= now && end >= now;
		const isPastEvent = end < now;
		const color = isActiveEvent ? 'success' : event.color;

		return {
			className: classNames({
				[`bg-l${darkModeStatus ? 'o25' : '10'}-${color} text-${color}`]: color,
				'border border-success': isActiveEvent,
				'opacity-50': isPastEvent,
			}),
		};
	};

	const formik = useFormik({
		initialValues: {
			eventName: '',
			eventStart: '',
			eventEnd: '',
			eventEmployee: '',
		},
		onSubmit: (values) => {
			if (eventAdding) {
				setEvents((prevEvents) => [
					...prevEvents,
					{
						id: values.eventStart,
						...getServiceDataWithServiceName(values.eventName),
						end: values.eventEnd,
						start: values.eventStart,
						user: { ...getUserDataWithUsername(values.eventEmployee) },
					},
				]);
			}
			setToggleInfoEventCanvas(false);
			setEventAdding(false);
			setEventItem(null);
			formik.setValues({});
		},
	});

	useEffect(() => {
		if (eventItem)
			formik.setValues({
				...formik.values,
				eventId: eventItem.id || null,
				eventName: eventItem.name,
				eventStart: moment(eventItem.start).format(),
				eventEnd: moment(eventItem.end).format(),
				eventEmployee: eventItem?.user?.username,
			});
		return () => {};
		//	eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventItem]);
    return (
		<div>
			<Card stretch style={{ minHeight: 500 }}>
				<CardHeader>
					<CardLabel icon='Today' iconColor='info'>
						<CardTitle>{moment(date).format('MMMM Do YYYY')}</CardTitle>
						<CardSubTitle>{moment(date).fromNow()}</CardSubTitle>
					</CardLabel>
					<CardActions>
						<CalendarTodayButton
							unitType={Views.DAY}
							date={date}
							setDate={setDate}
							viewMode={Views.DAY}
						/>
					</CardActions>
				</CardHeader>
				<CardBody isScrollable>
					<Calendar
						selectable
						toolbar={false}
						localizer={localizer}
						events={events}
						defaultView={Views.WEEK}
						views={views}
						view={Views.DAY}
						date={date}
						scrollToTime={new Date(1970, 1, 1, 6)}
						defaultDate={new Date()}
						onSelectEvent={(event) => {
							setInfoEvent();
							setEventItem(event);
						}}
						onSelectSlot={handleSelect}
						onView={handleViewMode}
						onDrillDown={handleViewMode}
						components={{
							event: MyEventDay, // used by each view (Month, Day, Week)
						}}
						eventPropGetter={eventStyleGetter}
					/>
				</CardBody>
			</Card>
		</div>
	);
}
